import {Injectable} from '@angular/core';
import {HttpClient, HttpEventType, HttpResponse} from '@angular/common/http';
import {Observable, of, ReplaySubject, Subject} from 'rxjs';
import {RequestModel} from '../models/request.model';
import {API} from '../../shared/constants/api.constants';
import {catchError, finalize, map, shareReplay, switchMap, tap} from 'rxjs/operators';
import {Warehouse} from '../models/warehouse.model';
import {ProductionPoModel} from '../models/production-po.model';
import {UserService} from '../../shared/services/user.service';
import {saveAs} from 'file-saver';
import cloneDeep from 'lodash/cloneDeep';
import {MilestoneItem, PrinterItemModel} from '../models/printer-item.model';
import {ErrorHandlingHttpClient} from '../../shared/services/error-handling-http-client.service';
import {UserModel} from '../../shared/models/user.model';
import {UtilsService} from '../../shared/services/utils.service';
import {UserGroups} from '../../shared/constants/user-groups.enum';
import {FILESCANCODE, FileUploadService} from './file-upload.service';
import {RequestTypeModel} from '../models/request-type.model';
import {MessageService} from '../../shared/services/message-service.service';
import {TranslateService} from '@ngx-translate/core';
import {Events, EventService} from '../../shared/services/event-service.service';
import {FileDownloadService} from './file-download.service';
import {LoaderService} from '../../shared/services/loader.service';

@Injectable({
  providedIn: 'root'
})
export class RequestsService {
  constructor(
    private http: HttpClient,
    private errorHandlingHttp: ErrorHandlingHttpClient,
    private fileUploadService: FileUploadService,
    private userService: UserService,
    private messageService: MessageService,
    private translateService: TranslateService,
    private eventService: EventService,
    private loaderService: LoaderService
  ) {
    this.warehousesObservable = this.replayAndClone(this.errorHandlingHttp.get(`${API.warehouses}`));
    this.companiesObservable = this.replayAndClone(this.errorHandlingHttp.get(`${API.companies}`));
    this.lineOfBusinessObservable = this.replayAndClone(this.errorHandlingHttp.get(`${API.lineOfBusinessTypes}`));
    this.channelTypesObservable = this.replayAndClone(this.errorHandlingHttp.get(`${API.chnTypes}`));
    this.inventoryTypesObservable = this.replayAndClone(this.errorHandlingHttp.get(`${API.inventoryTypes}`));
    this.requestTypeInventoryTypesObservable = this.replayAndClone(this.errorHandlingHttp.get(`${API.requestTypeInventoryTypes}`));
    this.europeanCountriesObservable = this.replayAndClone(this.errorHandlingHttp.get(`${API.europeCountries}`));
    this.printers = this.replayAndClone(this.errorHandlingHttp.get(`${API.printers}`));
    this.fileTypesWhiteList = this.replayAndClone(this.errorHandlingHttp.get(`${API.fileTypes}`));
    this.designerList = this.replayAndClone(
      this.errorHandlingHttp.get(`${API.getUsers}`, {params: {nameBeginning: '', groupKeys: UserGroups.Designer}})
    );
    this.creativeDirectorList = this.replayAndClone(
      this.errorHandlingHttp.get(`${API.getUsers}`, {params: {nameBeginning: '', groupKeys: UserGroups.CREATIVE_DIRECTOR}})
    );

    this.requestTypes$ = this.errorHandlingHttp.get<RequestTypeModel[]>(`${API.requestTypes}`).pipe(
      shareReplay({
        bufferSize: 1,
        refCount: false
      })
    );
  }

  // tslint:disable-next-line:no-unused-variable
  private requestDetails: ReplaySubject<RequestModel> = new ReplaySubject<RequestModel>(1);
  private requestName: ReplaySubject<string> = new ReplaySubject<string>(1);
  private poDetails: ReplaySubject<ProductionPoModel> = new ReplaySubject<ProductionPoModel>(1);

  private warehousesObservable: Observable<any>;
  private companiesObservable: Observable<any>;
  private lineOfBusinessObservable: Observable<any>;
  private channelTypesObservable: Observable<any>;
  private inventoryTypesObservable: Observable<any>;
  private requestTypeInventoryTypesObservable: Observable<any>;
  private europeanCountriesObservable: Observable<any>;
  private printers: Observable<any>;
  private fileTypesWhiteList: Observable<any>;
  private designerList: Observable<Array<UserModel>>;
  private creativeDirectorList: Observable<Array<UserModel>>;
  private wpnDetailsByWpnId = {};
  private requestTypes$;

  /**
   * @depreacted Rewrite using store
   */
  public createRequest(): Observable<RequestTypeModel[]> {
    return this.requestTypes$;
  }

  public updateRequestDetails(requestId: string | number, details: any, draft = false): Observable<RequestModel> {
    return this.errorHandlingHttp.put<RequestModel>(API.getRequestId(requestId, draft), details);
  }

  public postCreateNewRequest(requestName: string, requestType: string): Observable<RequestModel> {
    const requestObj = {
      name: requestName,
      type: requestType
    };
    this.resetRequestDetails();

    return this.errorHandlingHttp.post(`${API.createNewRequest}`, requestObj);
  }

  public downloadAttachment(requestId: string | number, attachmentId: string | number) {
    this.downloadFile(API.attachmentDownload(requestId, attachmentId));
  }

  public downloadPoPdf(requestId: string | number, poId: number, type: string): void {
    this.downloadFile(API.poPdfDownload(requestId, poId, type));
  }
  public fetchPoPdf(requestId: string | number, poId: number, type: string): Observable<[Blob, string]> {
    return this.errorHandlingHttp
      .get(API.poPdfDownload(requestId, poId, type), {
        observe: 'response',
        responseType: 'blob' as 'json'
      })
      .pipe(map(resp => [resp.body, FileDownloadService.getFileNameFromContentDispostion(resp.headers.get('content-disposition'))]));
  }

  public downloadFile(url: string, fileName?: string): void {
    this.errorHandlingHttp
      .get<HttpResponse<Blob>>(url, {
        observe: 'response',
        responseType: 'blob' as 'json'
      })
      .subscribe(resp => {
        const name = this.getFileNameFromHeader(resp) || fileName || this.getFileNameFromUrl(url);
        saveAs(resp.body, name);
      });
  }

  private getFileNameFromHeader(resp: HttpResponse<any>): string {
    if (resp.headers.has('file-name')) {
      return UtilsService.getFileNameFromHeaders(resp.headers);
    }
    return null;
  }

  private getFileNameFromUrl(url: string): string {
    url = decodeURIComponent(url);
    const base = url.split('?')[0];
    const components = base.split('/');
    // fileName
    return components[components.length - 1];
  }

  public downloadItemFile(item, requestId: number | string): void {
    this.downloadFile(this.getFileUrl(item, requestId));
  }

  public getFileUrl(item, requestId: number | string): string {
    let url: string;

    if (item.url) {
      return item.url;
    }

    switch (item.type) {
      case 'po':
      case 'sow':
      case 'printer':
        url = API.poPdfDownload(requestId, item.id, item.type);
        break;
      case 'file':
        url = API.getFile(item.id);
        break;
      default:
        url = API.attachmentDownload(requestId, item.attachmentId || item.id);
    }

    return url;
  }

  public getBlob(url: string): Observable<any> {
    return this.errorHandlingHttp.get(url, {
      observe: 'response',
      responseType: 'blob' as 'json'
    });
  }

  public getFileTypes(): Observable<any> {
    return this.fileTypesWhiteList;
  }

  public uploadProofOfPayments(requestId: string | number, file: File): Observable<any> {
    this.loaderService.showLoader();
    return this.fileUploadService
      .requestAttachmentUploadLink(requestId, 'finance', file)
      .pipe(
        switchMap(uploadLink =>
          this.fileUploadService
            .uploadFileWithProgress(uploadLink, file)
            .pipe(
              switchMap(event => {
                if (event.type === HttpEventType.Response) {
                  return this.fileUploadService.scanUploadFile(uploadLink.uploadId, file.name).pipe(
                    switchMap(res => {
                      if(res.code === FILESCANCODE.SAFE){
                        return this.createProofOfPayments(requestId, uploadLink.uploadId);
                      }
                    })
                  );
                }
                return of(event);
              })
            )
        )
      ).pipe(finalize(() => this.loaderService.hideLoader()));
  }

  public createProofOfPayments(requestId: string | number, uploadId: string): Observable<any> {
    return this.errorHandlingHttp.post(`${API.createProofOfPayment(requestId)}`, null, {
      reportProgress: true,
      observe: 'events',
      params: {uploadId}
    });
  }

  public uploadExternalPin(requestId: string | number, file: File, reqItemId: string | number, reqType: string): Observable<any> {
    this.loaderService.showLoader();
    return this.fileUploadService
      .requestAttachmentUploadLink(requestId, 'external-pin', file)
      .pipe(
        switchMap(uploadLink =>
          this.fileUploadService
            .uploadFileWithProgress(uploadLink, file)
            .pipe(
              switchMap(event => {
                if (event.type === HttpEventType.Response) {
                  return this.fileUploadService.scanUploadFile(uploadLink.uploadId, file.name).pipe(
                    switchMap(res => {
                      if(res.code === FILESCANCODE.SAFE){
                        return this.createExternalPin(requestId, uploadLink.uploadId, reqItemId, reqType);
                      }
                    })
                  );
                }
                return of(event);
              })
            )
        )
      ).pipe(finalize(() => this.loaderService.hideLoader()));
  }

  public createExternalPin(requestId: string | number, uploadId: string, reqItemId: string | number, reqType: string): Observable<any> {
    return this.errorHandlingHttp.post(`${API.createExternalPin[reqType](requestId, reqItemId)}`, null, {
      reportProgress: true,
      observe: 'events',
      params: {uploadId}
    });
  }

  public linkFileToReqItem(requestId: string | number, reqItemId: string | number, attachmentId: string | number): Observable<any> {
    /**
     * Important to use http client in order to catch errors
     */
    return this.http.post(`${API.linkAttachment(requestId, reqItemId)}`, [{attachmentId}], {
      headers: {
        username: this.userService.getCurrentUserName()
      }
    });
  }

  public updateAttachmentStatus(
    requestType: string,
    requestId: string | number,
    reqItemId: string | number,
    attachmentId: string | number,
    status: string,
    rejectionReason?: string,
    isPrinterProofs?: boolean
  ): Observable<any> {
    return this.errorHandlingHttp.put(
      `${API.updateAttachmentStatus[requestType](requestId, reqItemId, attachmentId, isPrinterProofs)}`,
      {attachmentId, status, rejectionReason},
      {
        headers: {
          username: this.userService.getCurrentUserName()
        }
      }
    );
  }

  public updateItemStatus(
    requestType: string,
    requestId: string | number,
    reqItemId: string | number,
    status: string,
    rejectionReason?: string,
    isPrinterProofs?: boolean,
    requestStage?: string
  ): Observable<any> {
    return this.errorHandlingHttp.put(
      `${API.updateRequestItemStatus[requestType](requestId, reqItemId, isPrinterProofs, requestStage)}`,
      {status, rejectionReason},
      {
        headers: {
          username: this.userService.getCurrentUserName()
        }
      }
    );
  }

  public deleteProofOfPayment(requestId: string | number, attachmentId: string | number): Observable<void> {
    return this.errorHandlingHttp.delete<void>(`${API.deleteProofOfPayment(requestId, attachmentId)}`);
  }

  public deleteExternalPin(
    requestId: string | number,
    attachmentId: string | number,
    itemId: string | number,
    reqType: string
  ): Observable<void> {
    return this.errorHandlingHttp.delete<void>(`${API.deleteExternalPin[reqType](requestId, attachmentId, itemId)}`);
  }

  public uploadSOWFile(requestId: string | number, poId: string | number, file: File): Observable<any> {
    this.loaderService.showLoader();
    return this.fileUploadService
      .requestAttachmentUploadLink(requestId, 'sow', file)
      .pipe(
        switchMap(uploadLink =>
          this.fileUploadService
            .uploadFileWithProgress(uploadLink, file)
            .pipe(
              switchMap(event => {
                if (event.type === HttpEventType.Response) {
                  return this.fileUploadService.scanUploadFile(uploadLink.uploadId, file.name).pipe(
                    switchMap(res => {
                      if(res.code === FILESCANCODE.SAFE){
                        return this.createSowAttachment(requestId, poId, uploadLink.uploadId);
                      }
                    })
                  );
                }
                return of(event);
              })
            )
        )
      ).pipe(finalize(() => this.loaderService.hideLoader()));
  }

  private createSowAttachment(requestId: string | number, poId: string | number, uploadId: string): Observable<any> {
    return this.errorHandlingHttp.post(`${API.createSowAttachment(requestId, poId)}`, null, {
      reportProgress: true,
      observe: 'events',
      params: {uploadId}
    });
  }

  public deleteSOWFile(requestId: string | number, poId: string | number, attachmentId: string | number): Observable<any> {
    return this.errorHandlingHttp.delete<void>(`${API.deleteSOWFile(requestId, poId, attachmentId)}`);
  }

  public getUsers(nameBeginning: string, groupKeys?: string[]): Observable<UserModel[]> {
    /**
     * Important to use http client in order to catch errors
     */
    const queryParams = {
      params: {
        nameBeginning,
        ...(groupKeys && {groupKeys})
      }
    };
    return this.http.get<UserModel[]>(`${API.getUsers}`, queryParams);
  }

  public getDesignerList(): Observable<Array<UserModel>> {
    return this.designerList;
  }

  public getCreativeDirectorList(): Observable<Array<UserModel>> {
    return this.creativeDirectorList;
  }

  public getWPNdata(WPN: number): Observable<any> {
    if (!this.wpnDetailsByWpnId[WPN]) {
      /**
       * Important to use http client in order to catch errors
       */
      this.wpnDetailsByWpnId[WPN] = this.replayAndClone(this.http.get(`${API.getWPNdetails(WPN)}`));
    }
    return this.wpnDetailsByWpnId[WPN];
  }

  public getEuropeCountriesList(): Observable<any> {
    return this.europeanCountriesObservable;
  }

  public getMerchants(name: string, limit?: number): Observable<any> {
    const queryParams = {
      params: {
        merchantName: name
      }
    };
    return this.errorHandlingHttp.get(`${API.getMerchants}`, queryParams);
  }

  public getMerchantInfo(id: number): Observable<any> {
    /**
     * Important to use http client in order to catch errors
     */
    return this.http.get(`${API.getMerchantInfo(id)}`);
  }

  public getContacts(nameBeginning: string): Observable<any> {
    const queryParams = {
      params: {
        nameBeginning
      }
    };
    return this.errorHandlingHttp.get(`${API.getContacts}`, queryParams);
  }

  public getShipments(): Observable<Array<Warehouse>> {
    return this.warehousesObservable;
  }

  public deleteRequest(id: string): Observable<any> {
    return this.errorHandlingHttp.delete(`${API.getRequestId(id)}`);
  }

  public updateRequest(id: string | number, request: any): Observable<RequestModel> {
    return this.errorHandlingHttp.put<RequestModel>(API.getRequestId(id), request);
  }

  public updateRequestName(id: string, name: string): Observable<any> {
    const observable = new Subject();

    this.errorHandlingHttp.put(API.updateRequestName(id), {value: name}).subscribe(
      (data: {value: string}) => {
        this.requestName.next(data.value);
        observable.next(data);
      },
      err => observable.error(err)
    );

    return observable.asObservable();
  }

  public getRequestNameListener(): Observable<any> {
    return this.requestName.asObservable();
  }

  public getPOListener(): Observable<any> {
    return this.poDetails.asObservable();
  }

  public callPO(requestId: number | string): void {
    this.http.get(`${API.managePO(requestId)}`).subscribe((data: ProductionPoModel) => {
      this.poDetails.next(data);
    });
    this.eventService.emit({eventName: Events.UpdateActionHistory});
  }

  public performAction(
    requestId: string | number,
    actionType: string,
    stageCode: string,
    comment?: string,
    attachmentUploadIds?: string[]
  ): Observable<HttpResponse<any>> {
    return this.errorHandlingHttp.post(
      `${API.performAction(requestId)}`,
      {
        actionType,
        stageCode,
        comment,
        attachmentUploadIds
      },
      {
        headers: {
          username: this.userService.getCurrentUserName()
        },
        observe: 'response'
      }
    );
  }

  public createPO(requestId: number | string, data: any): Observable<any> {
    const reqObj = Object.assign(data);
    return this.errorHandlingHttp.post(`${API.managePO(requestId)}`, reqObj);
  }

  public updatePO(requestId: number | string, poId: number, data: any): Observable<any> {
    const observable = new Subject();

    const reqObj = Object.assign(data);
    this.errorHandlingHttp.put(`${API.updatePO(requestId, poId)}`, reqObj).subscribe(
      response => {
        this.callPO(requestId);
        observable.next(response);
      },
      error => observable.error(error)
    );
    return observable.asObservable();
  }

  public deletePO(requestId: number | string, poId: number): Observable<any> {
    const observable = new Subject<void>();

    this.errorHandlingHttp.delete(`${API.updatePO(requestId, poId)}`).subscribe(
      () => {
        this.callPO(requestId);
        observable.next();
      },
      err => observable.error(err)
    );

    return observable.asObservable();
  }

  public getPrinters(): Observable<any> {
    return this.printers;
  }

  public getCompanies(): Observable<any> {
    return this.companiesObservable;
  }

  public getChnTypes(): Observable<any> {
    return this.channelTypesObservable;
  }

  public getInventoryTypes(): Observable<any> {
    return this.inventoryTypesObservable;
  }

  public getRequestTypeInventoryTypes(): Observable<any> {
    return this.requestTypeInventoryTypesObservable;
  }

  public getLinesOfBusinessTypes(): Observable<any> {
    return this.lineOfBusinessObservable;
  }

  private replayAndClone(objectObservable: Observable<any>) {
    // cloning to avoid data corruption
    return objectObservable.pipe(
      shareReplay({
        refCount: false,
        bufferSize: 1
      }),
      map(data => cloneDeep(data))
    );
  }

  private resetRequestDetails(): void {
    this.requestDetails = new ReplaySubject<RequestModel>(1);
  }

  public getAPIversion(): Observable<string> {
    return this.getVersions(API.version);
  }

  public getUIversion(): Observable<string> {
    return this.getVersions(API.uiVersion);
  }

  public getFnVersion(): Observable<string> {
    return this.getVersions(API.fnVersion);
  }

  private getVersions(versionsUri: string): Observable<string> {
    const httpOptions = {
      responseType: 'text' as 'json'
    };

    return this.http.get<string>(versionsUri, httpOptions).pipe(catchError(() => of('')));
  }

  public getRequestPrinterWorkItems<T = any>(requestId: number | string): Observable<T> {
    return this.http.get<T>(`${API.getRequestPrinterWorkItems(requestId)}`);
  }

  /**
   * @todo move to AtPrinterService implementation
   */
  public uploadCardProductionWorkItemMilestoneAsn(
    requestId: string,
    printerId: number,
    itemId: number,
    milestoneId: number,
    file: File
  ): Observable<any> {
    this.loaderService.showLoader();
    return this.fileUploadService
      .requestAttachmentUploadLink(requestId, 'asn', file)
      .pipe(
        switchMap(uploadLink =>
          this.fileUploadService
            .uploadFileWithProgress(uploadLink, file)
            .pipe(
              switchMap(event => {
                if (event.type === HttpEventType.Response) {
                  return this.fileUploadService.scanUploadFile(uploadLink.uploadId, file.name).pipe(
                    switchMap(res => {
                      if(res.code === FILESCANCODE.SAFE){
                        return this.createCardProductionWorkItemMilestoneAsn(
                          requestId, printerId, itemId, milestoneId, uploadLink.uploadId);
                      }
                    })
                  );
                }
                return of(event);
              })
            )
        )
      ).pipe(finalize(() => this.loaderService.hideLoader()));
  }

  /**
   * @todo move to AtPrinterService implementation
   */
  private createCardProductionWorkItemMilestoneAsn(
    requestId: string,
    printerId: number,
    itemId: number,
    milestoneId: number,
    uploadId: string
  ): Observable<any> {
    return this.http.post(`${API.createCardProductionWorkItemMilestoneAsn(requestId, printerId, itemId, milestoneId)}`, null, {
      reportProgress: true,
      observe: 'events',
      params: {uploadId}
    });
  }

  public deleteCardProductionWorkItemMilestoneAsn(
    requestId: string,
    printerId: number,
    itemId: number,
    milestoneId: number,
    attachmentId: number
  ): Observable<void> {
    return this.errorHandlingHttp.delete<void>(
      `${API.deleteCardProductionWorkItemMilestoneAsn(requestId, printerId, itemId, milestoneId, attachmentId)}`
    );
  }

  public updateRequestStatus(requestId: number | string, status: string): any {
    const reqObj = {value: status};

    return this.errorHandlingHttp.put(`${API.updateRequestStatus(requestId)}`, reqObj);
  }

  public bulkUpdateCardProductionAtPrinter(requestId: string, printerId: number, itemId: number, data: any) {
    return this.errorHandlingHttp.put(`${API.doCardProductionBulkWorkItemMilestoneUpdate(requestId, printerId, itemId)}`, data);
  }

  public bulkUpdatePopProductionAtPrinter(requestId: string, printerId: number, itemId: number, data: any) {
    return this.errorHandlingHttp.put(`${API.doPopProdBulkWorkItemMilestoneUpdate(requestId, printerId, itemId)}`, data);
  }

  public updateMilestoneDetails(
    requestId: number | string,
    printerItem: PrinterItemModel,
    milestone: MilestoneItem,
    data: any
  ): Observable<any> {
    return this.errorHandlingHttp.put(
      API.updateMilestoneDetails(requestId, printerItem.printerDetails.id, printerItem.id, milestone.id),
      data
    );
  }

  public uploadArtworkAttachment(requestId: number | string, file: File): Observable<any> {
    this.loaderService.showLoader();
    return this.fileUploadService
      .requestAttachmentUploadLink(requestId, 'art', file)
      .pipe(
        switchMap(uploadLink => this.fileUploadService.uploadFileWithProgress(uploadLink, file)
            .pipe(
              switchMap(event => {
                if (event.type === HttpEventType.Response) {
                  return this.fileUploadService.scanUploadFile(uploadLink.uploadId, file.name).pipe(
                    switchMap(res => {
                      if(res.code === FILESCANCODE.SAFE){
                        return this.createArtworkAttachment(uploadLink.uploadId, requestId);
                      }
                    })
                  );
                }
                return of(event);
              })
            )
        )
      ).pipe(finalize(() => this.loaderService.hideLoader()));
  }

  private createArtworkAttachment(uploadId: string, requestId: number | string): Observable<any> {
    return this.errorHandlingHttp.post(API.createArtworkAttachment(requestId), null, {
      reportProgress: true,
      observe: 'events',
      params: {uploadId}
    });
  }

  public deleteArtworkAttachment(requestId: number | string, attachmentId: number): Observable<any> {
    return this.errorHandlingHttp.delete(API.deleteArtworkAttachment(requestId, attachmentId));
  }

  public duplicateRequest(requestId: number | string): Observable<RequestModel | never> {
    return this.errorHandlingHttp.post(API.duplicateRequest(requestId), {}).pipe(
      tap(() => {
        this.messageService.showSystemMessage(
          'success',
          null,
          this.translateService.instant('common.message.request-successfully-created', {requestId})
        );
      })
    );
  }

  public getRequestDetails(requestId: number | string): Observable<RequestModel | never> {
    this.loaderService.showLoader();
    return this.errorHandlingHttp.get(API.getRequestDetails(requestId), {}).pipe(finalize(() => this.loaderService.hideLoader()));
  }
}
